import { useCallback } from "react";
import { useMutationWrapper } from "@SeemplicityDev/shared";
import {
  DELETE_FINDING_SCOPE,
  DELETE_SCOPE_GROUP,
  EDIT_SCOPE_GROUP,
  MOVE_FINDING_SCOPE,
  MOVE_SCOPE_GROUP,
  RENAME_FINDING_SCOPE,
  type DeleteScopeResponse,
  type DeleteScopeGroupResponse,
  type EditScopeGroupResponse,
  type MoveScopeGroupResponse,
  type MoveScopeResponse,
  type RenameScopeResponse,
  type ScopeWritePayload,
} from "./ScopeGuardrailsApi";
import type { NameCollision } from "./types";

export interface UseDeleteScopeReturnValue {
  // Rejects on any failure, including a dependency block (the BE raises; it never
  // returns ok:false for a scope delete) — callers wrap in try/catch and re-check the
  // dependencies to show the reason. Resolves false only as a defensive fallback when
  // the payload is missing.
  deleteScope: (id: string) => Promise<boolean>;
  loading: boolean;
}

export const useDeleteScope = (): UseDeleteScopeReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<DeleteScopeResponse>(
    DELETE_FINDING_SCOPE,
    { refetchQueries: ["scopes"] }
  );
  const deleteScope = useCallback(
    async (id: string): Promise<boolean> => {
      const res = await mutate({ variables: { id } });
      return res?.data?.delete_finding_scope_v3?.ok ?? false;
    },
    [mutate]
  );
  return { deleteScope, loading };
};

export interface DeleteScopeGroupOptions {
  deleteDescendants?: boolean;
}

export interface DeleteScopeGroupResult {
  ok: boolean;
  // Names already taken at the top level, when a keep-descendants delete is refused.
  nameCollisions: NameCollision[];
}

export interface UseDeleteScopeGroupReturnValue {
  // ok:false means exactly one thing: a keep-descendants delete refused because a
  // child's name is already taken at the top level (nameCollisions populated). A
  // dependency block rejects, as for the scope delete.
  deleteScopeGroup: (
    id: string,
    options?: DeleteScopeGroupOptions
  ) => Promise<DeleteScopeGroupResult>;
  loading: boolean;
}

export const useDeleteScopeGroup = (): UseDeleteScopeGroupReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<DeleteScopeGroupResponse>(
    DELETE_SCOPE_GROUP,
    { refetchQueries: ["scopes"] }
  );
  const deleteScopeGroup = useCallback(
    async (
      id: string,
      { deleteDescendants = false }: DeleteScopeGroupOptions = {}
    ): Promise<DeleteScopeGroupResult> => {
      const res = await mutate({ variables: { id, deleteDescendants } });
      const payload = res?.data?.delete_scope_group_v3;
      return {
        ok: payload?.ok ?? false,
        nameCollisions: payload?.name_collisions ?? [],
      };
    },
    [mutate]
  );
  return { deleteScopeGroup, loading };
};

// Shared by every write that can be refused on a name collision. ok:false means
// exactly that (nameCollisions populated); anything else rejects — callers wrap in
// try/catch. A missing payload is treated as refused.
export type ScopeWriteResult = DeleteScopeGroupResult;

const toWriteResult = (
  payload: ScopeWritePayload | null | undefined
): ScopeWriteResult => ({
  ok: payload?.ok ?? false,
  nameCollisions: payload?.name_collisions ?? [],
});

const SCOPE_TREE_REFETCH = { refetchQueries: ["scopes"] };

export interface UseRenameScopeReturnValue {
  renameScope: (id: string, name: string) => Promise<ScopeWriteResult>;
  loading: boolean;
}

export const useRenameScope = (): UseRenameScopeReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<RenameScopeResponse>(
    RENAME_FINDING_SCOPE,
    SCOPE_TREE_REFETCH
  );
  const renameScope = useCallback(
    async (id: string, name: string): Promise<ScopeWriteResult> => {
      const res = await mutate({ variables: { id, name } });
      return toWriteResult(res?.data?.rename_finding_scope_v3);
    },
    [mutate]
  );
  return { renameScope, loading };
};

export interface MoveScopeOptions {
  // Omit (or null) to move to the top level.
  scopeGroupId?: string | null;
}

export interface UseMoveScopeReturnValue {
  moveScope: (
    id: string,
    options?: MoveScopeOptions
  ) => Promise<ScopeWriteResult>;
  loading: boolean;
}

export const useMoveScope = (): UseMoveScopeReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<MoveScopeResponse>(
    MOVE_FINDING_SCOPE,
    SCOPE_TREE_REFETCH
  );
  const moveScope = useCallback(
    async (
      id: string,
      { scopeGroupId = null }: MoveScopeOptions = {}
    ): Promise<ScopeWriteResult> => {
      const res = await mutate({ variables: { id, scopeGroupId } });
      return toWriteResult(res?.data?.move_finding_scope_v3);
    },
    [mutate]
  );
  return { moveScope, loading };
};

export interface MoveScopeGroupOptions {
  // Omit (or null) to move the group and its subtree to the top level.
  parentGroupId?: string | null;
}

export interface UseMoveScopeGroupReturnValue {
  moveScopeGroup: (
    id: string,
    options?: MoveScopeGroupOptions
  ) => Promise<ScopeWriteResult>;
  loading: boolean;
}

export const useMoveScopeGroup = (): UseMoveScopeGroupReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<MoveScopeGroupResponse>(
    MOVE_SCOPE_GROUP,
    SCOPE_TREE_REFETCH
  );
  const moveScopeGroup = useCallback(
    async (
      id: string,
      { parentGroupId = null }: MoveScopeGroupOptions = {}
    ): Promise<ScopeWriteResult> => {
      const res = await mutate({ variables: { id, parentGroupId } });
      return toWriteResult(res?.data?.move_scope_group_v3);
    },
    [mutate]
  );
  return { moveScopeGroup, loading };
};

export interface EditScopeGroupInput {
  name: string;
  // The group's whole membership after the edit; members left out move to the top level.
  childGroupIds: string[];
  childScopeIds: string[];
}

export interface UseEditScopeGroupReturnValue {
  editScopeGroup: (
    id: string,
    input: EditScopeGroupInput
  ) => Promise<ScopeWriteResult>;
  loading: boolean;
}

export const useEditScopeGroup = (): UseEditScopeGroupReturnValue => {
  const [mutate, { loading }] = useMutationWrapper<EditScopeGroupResponse>(
    EDIT_SCOPE_GROUP,
    SCOPE_TREE_REFETCH
  );
  const editScopeGroup = useCallback(
    async (
      id: string,
      { name, childGroupIds, childScopeIds }: EditScopeGroupInput
    ): Promise<ScopeWriteResult> => {
      const res = await mutate({
        variables: { id, name, childGroupIds, childScopeIds },
      });
      return toWriteResult(res?.data?.edit_scope_group_v3);
    },
    [mutate]
  );
  return { editScopeGroup, loading };
};
